import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { nextPrompt, type PersonalizationPrompt } from '../domain/prompts';
import { track } from '../lib/analytics';
import { useLearned } from '../state/selectors';
import { useStore } from '../state/store';

/**
 * One occasional question that refines the profile, shown inline (never as a pop-up) on Today.
 * Chosen once per visit so it stays put while answered; answered or dismissed, it never returns.
 */
export function PromptCard() {
  const learned = useLearned();
  const resolve = useStore((s) => s.resolvePrompt);
  const markShown = useStore((s) => s.markPromptShown);
  const showToast = useStore((s) => s.showToast);
  const [prompt] = useState<PersonalizationPrompt | null>(() => {
    const s = useStore.getState();
    return nextPrompt({
      profile: s.profile, cookedCount: s.cooked.length, feedback: s.feedback, kitchenCount: s.kitchen.items.length,
      hasPlan: !!s.plan || s.planHistory.length > 0, preferQuick: learned.effects.preferQuick, onboardedAt: s.onboardedAt, now: new Date(),
    });
  });
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!prompt) return;
    markShown();
    track('prompt_shown', { prompt: prompt.id });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!prompt || done) return null;
  const answer = (i: number) => {
    resolve(prompt.id, prompt.options[i].patch(useStore.getState().profile));
    track('prompt_answered', { prompt: prompt.id, option: i });
    setDone(true);
    showToast('Thanks. Your picks are updated.');
  };
  const dismiss = () => {
    resolve(prompt.id, null);
    track('prompt_dismissed', { prompt: prompt.id });
    setDone(true);
  };

  return (
    <section className="tile p-4 mb-8 fade-in" aria-labelledby="prompt-q">
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <h2 id="prompt-q" className="text-headline">{prompt.question}</h2>
          <p className="text-subhead text-fg-muted mt-0.5">{prompt.why}</p>
        </div>
        <button className="icon-btn -mr-2 -mt-2 text-fg-muted" onClick={dismiss} aria-label="Not now"><X size={18} aria-hidden /></button>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {prompt.options.map((o, i) => <button key={o.label} className="chip" onClick={() => answer(i)}>{o.label}</button>)}
      </div>
      <Link to="/you/personalization" className="inline-block text-footnote text-fg-muted mt-3 min-h-[24px]">Fewer questions? Manage them in Profile</Link>
    </section>
  );
}
